<template>
    <div v-if="tierPrices.length > 1" class="">
        <div v-for="(tierPrice, index) in tierPrices" :key="index">
            <div v-if="index === 0" class="flex flex-col">
                <span class="text-xs text-base-content-subtle">{{ $t('product.price.until') }} {{ tierPrice.quantity }} {{ $t('product.price.pcs') }}</span>
                <span class="text-neutral font-semibold text-xl">{{ getFormattedPrice(tierPrice.unitPrice) }}</span>
            </div>
            <div v-else>
                {{ $t('product.price.from') }} {{ tierPrices[index - 1]!.quantity + 1 }} {{ $t('product.price.pcs') }} {{ getFormattedPrice(tierPrice.unitPrice) }} <span v-if="index < tierPrices.length - 1">|</span>
            </div>
        </div>
    </div>

    <div v-else class="flex flex-col">
        <div v-if="price?.listPrice?.percentage" class="badge badge-sale text-xs rounded-xs px-2 mb-1">
            -{{ Math.round(price?.listPrice?.percentage) }}%
        </div>

        <div v-if="price?.unitPrice" class="flex gap-3 items-baseline" data-cy="product-detail-regular-price">
            <span class="text-neutral font-semibold text-2xl" :class="{ 'text-sale': price?.listPrice?.price}">
                <span v-if="displayFrom">{{ $t('product.price.from') }}</span> {{ getFormattedPrice(price?.unitPrice) }}
            </span>

            <div v-if="price?.listPrice?.price" class="line-through text-sm">
                {{ $t('product.price.rrp') }}: {{ getFormattedPrice(price?.listPrice?.price) }}
            </div>
        </div>

        <div v-if="price?.regulationPrice" class="text-xs text-placeholder mb-2">
            {{ $t('product.price.regulation') }}: <span v-if="displayFrom">{{ $t('product.price.regulationFrom') }}</span><span v-else>{{ getFormattedPrice(price?.regulationPrice?.price) }}</span>
        </div>

        <div
            v-if="specialOfferPrice && !displayFrom"
            class="price-next-week text-xxs mt-1 mb-2 font-semibold text-secondary-content">
            {{ $t('product.listing.card.badges.specialOfferPrice') }} {{ getFormattedPrice(specialOfferPrice) }}
        </div>

        <client-only>
            <div class="flex flex-col text-xs text-base-content-subtle">
                <span v-if="sessionContext?.currentCustomerGroup?.displayGross">
                    {{ $t('product.price.inclTax') }}
                </span>
                <span v-else>
                    {{ $t('product.price.exclTax') }}
                </span>

                <MiscLink
                    :to="$t('product.price.shippingLink')"
                    external
                    target="_blank"
                    class="link link-hover leading-6"
                >
                    <template
                        v-if="currentShippingCountryId != null
                            && freeDeliveryByShippingCountry[currentShippingCountryId] != null
                            && freeDeliveryByShippingCountry[currentShippingCountryId]?.trim() !== ''"
                    >
                        {{ $t(freeDeliveryByShippingCountry[currentShippingCountryId]!) }}
                    </template>
                    <template v-else>
                        {{ $t(freeDeliveryByShippingCountry.fallback!) }}
                    </template>
                </MiscLink>
            </div>
            <template #fallback>
                <MiscSkeleton repeat="1" size="small" height="44px" />
            </template>
        </client-only>
    </div>
</template>

<script setup lang="ts">
const props = defineProps<{
    allOptionsSelected: boolean;
    isDynamicBuyableVoucher: boolean;
}>()

const { product } = useProduct()
const { displayFromVariants, price: composablePrice, tierPrices } = useProductPriceCustom(product)
const { getFormattedPrice } = usePrice()
const { sessionContext } = useSessionContext()
const price = computed(() => {
    // display from if isDynamicBuyableVoucher 
    // display from word not if allOptionsSelected 
    // display from word if product.variantsFrom 
    return props?.allOptionsSelected ? composablePrice.value : displayFromVariants.value ? product.value?.calculatedCheapestPrice : composablePrice.value
})
const currentShippingCountryId = computed(() => {
    return sessionContext?.value?.shippingLocation?.country?.id
})
const specialOfferPrice = computed(() => {
    return product?.value?.extensions?.badges?.specialPrice?.amount
})
const displayFrom = computed(() => {
    return props?.isDynamicBuyableVoucher || (props?.allOptionsSelected ? false : displayFromVariants.value)
})

const freeDeliveryByShippingCountry = ref<Record<string, string>>({
    '23f1e3a2eba64c5197e47e7e5e41e785': 'product.price.freeShippingCh', // CH
    'd1f39aad6c554a3183d35ba7a339a963': 'product.price.freeShippingCh', // LI (same as CH)
    'cf949571b0ec4c8badf1fe72e113551b': 'product.price.freeShippingSk',
    '56ff618e238148ceac926cee50bfacbf': 'product.price.freeShippingDk',
    '0496f34fc43f4734afe6de8f86cbad06': 'product.price.freeShippingPl',
    'e8cb959a1c014e00a4c348d5fb4f037d': 'product.price.freeShippingCz',
    '5dd54cdb31ce4a46a602a15db3cfd3c0': 'product.price.freeShippingFi',
    '9feacc46dc1549f98d4a4699bf5d1d9d': 'product.price.freeShippingNo',
    'fallback': 'product.price.freeShipping'
})
</script>
